import {Autocomplete, Box, Chip, Grid, TextField, Typography} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import CDatePicker from "crqlar-react-common/dist/components/CDatePicker";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import ReservationDetailsWrapper from "../ReservationDetailsWrapper";
import {useCurrentApp} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {useServicesForSector} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {
    useAllResourceCategoriesForSector
} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {
    useAllRootResourcesForSector,
    useResourcesWithAvailability
} from "crqlar-react-common/dist/utils/hooks/resources/useAllRootResources";
import {ResourceAvailabilityDTO} from "crqlar-react-common/dist/models/resource/resourceAvailability.model";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {isResourceAvailable} from "crqlar-react-common/dist/utils/resource.util";
import {ServiceType} from "crqlar-react-common/dist/models/service/serviceListRequest.dto";
import {addMinutes} from "crqlar-react-common/dist/utils/date.util";
import {Rate} from "crqlar-react-common/dist/models/rate/rate.model";
import {
    autoAssignResourcesForService,
    releaseAutoAssignedResources
} from "../../../../utils/serviceResourceAutoAssign.util";

interface Props {
    reservation?: Reservation
    setReservation: (reservation?: Reservation) => void
    // availabilityForActiveReservation?: RestaurantAvailableTablesResponse
    // loadingAvailabilities: boolean,
    onSave?: (allowNotify?: boolean) => void,
    onResend?: () => void,
    ageCategories?: AgeCategory[]
    onClose: () => void,
    onShowFolios?: () => void
    onShowLogs?: () => void
    serviceInfo?: Service
    onOpenCommunication?: () => void
    onOpenFormSelector?: () => void
    originalReservation?: Reservation,
    rates?: Rate[] | undefined
    loading?: boolean
    onSaveAndSendNoShowLink?: () => void
}

const ReservationDetailsAccommodation = ({
                                             reservation,
                                             setReservation,
                                             // availabilityForActiveReservation,
                                             // loadingAvailabilities,
                                             onSave,
                                             onResend,
                                             ageCategories,
                                             onClose,
                                             onShowFolios,
                                             onShowLogs,
                                             serviceInfo,
                                             onOpenCommunication,
                                             onOpenFormSelector,
                                             originalReservation,
                                            rates,
                                            loading,
                                            onSaveAndSendNoShowLink
                                         }: Props) => {

    const {t} = useTranslation()
    const [canEdit, setCanEdit] = useState(true)
    const appData = useCurrentApp()
    const sector = useSector(reservation?.sectorId ?? appData?.configuration?.sectorId)
    const services = useServicesForSector(sector?.id,  ServiceType.REGULAR)
    const resources = useAllRootResourcesForSector(sector?.id)
    const resourceCategories = useAllResourceCategoriesForSector(sector?.id)

    const resourcesWithAvailability =
        useResourcesWithAvailability({
            expectedStartDate: reservation?.expectedStartDate,
            expectedEndDate: reservation?.expectedEndDate,
            sectorId: sector?.id
        })

    const resourceIdsKey = reservation?.resourceIds?.join(',')

    // Resources the service requirements picked for the user - released again when
    // another service is chosen, so only the manual picks survive a service switch
    const autoAssignedResourceIdsRef = useRef<string[]>([])

    useEffect(() => {
        if (reservation?.serviceId || !resourceIdsKey || !services.length || !resources.length) return

        const reservationResources = resources.filter(r => reservation?.resourceIds?.includes(r.id as string))
        const firstAssociatedService = services.find((service) =>
            reservationResources.some(resource => service.resourceIds?.includes(resource.id as string))
        )

        if (firstAssociatedService) {
            setReservation({
                ...reservation,
                serviceId: firstAssociatedService.id,
                // Keep the time frame chosen by the user (e.g. calendar drag-to-create);
                // only derive the end date from the service duration when none was set
                expectedEndDate: reservation?.expectedEndDate
                    ?? addMinutes(reservation?.expectedStartDate, firstAssociatedService.durationMinutes || 60)
            })
        }
    }, [services, resources, resourceIdsKey, reservation?.serviceId])

    const renderDateAndTimeStart = () => {
        return (
            <Box
                flex={1}
            >
                <CDatePicker
                    value={reservation?.expectedStartDate}
                    marked={[
                        {
                            date: reservation?.expectedStartDate
                        }
                    ]}
                    onChange={(date) => {
                        //  updateField('date', date.value as Date)
                        setReservation({
                            ...reservation,
                            expectedStartDate: date
                        })
                    }}
                    disabled={!canEdit}
                    label={t('Check in')}
                />
            </Box>
        )
    }

    const renderDateAndTimeEnd = () => {
        return (
            <Box
                flex={1}
            >
                <CDatePicker
                    value={reservation?.expectedEndDate}
                    marked={[
                        {
                            date: reservation?.expectedEndDate
                        }
                    ]}
                    onChange={(date) => {
                        //  updateField('date', date.value as Date)
                        setReservation({
                            ...reservation,
                            expectedEndDate: date
                        })
                    }}
                    disabled={!canEdit}
                    label={t('Check out')}
                />
            </Box>
        )
    }

    const renderService = () => {
        return (
            <Autocomplete
                sx={{
                    '& fieldset': {
                        height: '4em',
                    },
                    '& .MuiAutocomplete-input': {
                        height: '2.2em',
                        lineHeight: '2.2em',
                    },
                }}
                id={'service'}
                options={services}
                size={'small'}
                getOptionLabel={(option: Service) => option.name as string}
                value={services.find(s => s.id === reservation?.serviceId) || null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Service')}
                    />
                )}

                fullWidth
                onChange={(e, value) => {
                    // Free the resources the previous service assigned before filling
                    // the requirements of the newly picked one
                    const keptResourceIds = releaseAutoAssignedResources(
                        reservation?.resourceIds,
                        autoAssignedResourceIdsRef.current
                    )

                    const {resourceIds, addedResourceIds} = autoAssignResourcesForService({
                        service: value || undefined,
                        resources,
                        selectedResourceIds: keptResourceIds,
                        resourcesWithAvailability
                    })

                    autoAssignedResourceIdsRef.current = addedResourceIds

                    setReservation({
                        ...reservation,
                        serviceId: value?.id,
                        resourceIds,
                        // Every time service changes - we update the end time based on the duration of the service
                        expectedEndDate: addMinutes(reservation?.expectedStartDate, value?.durationMinutes || 60)
                    })
                }}
            />
        )
    }

    const renderResources = () => {
        return (
            <Autocomplete
                sx={{
                    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                        minHeight: '4em',
                        paddingInline: '8px',
                    }
                }}
                id={'categories'}
                multiple

                // Sorting by availability & keeping the collection immutable
                options={[...(resources || [])].sort((a, b) => {
                    const aAvailable = isResourceAvailable(a, resourcesWithAvailability, reservation)
                    const bAvailable = isResourceAvailable(b, resourcesWithAvailability, reservation)

                    if (aAvailable === bAvailable) return 0
                    return aAvailable ? -1 : 1
                })}
                size={'small'}
                getOptionLabel={(option: Resource) => {
                    const categories = resourceCategories?.filter(c => option.categoryIds?.includes(c.id!))
                    const categoryNames = categories?.map(c => c.name).join(', ')
                    return [option.name || '', categoryNames].filter(Boolean).join(' - ')
                }}
                value={reservation?.resourceIds?.map(id => resources?.find(c => c.id === id)).filter(Boolean) as Resource[] || []}
                onOpen={() => {
                    // Force scroll to top when dropdown opens
                    setTimeout(() => {
                        const listbox = document.querySelector('#categories-listbox')
                        if (listbox) {
                            listbox.scrollTop = 0
                        }
                    }, 1)
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size={'small'}
                        label={t('Resources')}
                        InputLabelProps={{
                            sx: {
                                transform: 'translate(14px, 16px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                    transform: 'translate(14px, -11px) scale(0.75)'
                                }
                            }
                        }}
                    />
                )}

                // Rendering options based on availability and resource attachment
                renderOption={(props, option: Resource) => {

                    const backgroundColor = isResourceAvailable(option, resourcesWithAvailability, reservation) ? CommonThemeColors.sys.success.light
                        : CommonThemeColors.sys.error.light

                    return (
                        <Box component="li" {...props} key={option.id} style={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                                label={option.name}
                                style={{
                                    backgroundColor,
                                    color: 'black',
                                }}
                            />
                        </Box>
                    );
                }}

                fullWidth
                onChange={(e, value, reason, details) => {
                    // If user removes the resource - it becomes available again until he picks it again
                    if (reason === 'removeOption') {
                        const removedOption = details?.option;
                        const resourceWithAvailability = resourcesWithAvailability.find(resource => resource.id === removedOption?.id) as ResourceAvailabilityDTO

                        if(resourceWithAvailability) {
                            resourceWithAvailability.available = true
                        }
                    }

                    const newResources = [...value.map((v: any) => v.id)]

                    // Anything the user touches becomes his own pick and must not be
                    // released by the next service change
                    autoAssignedResourceIdsRef.current = autoAssignedResourceIdsRef.current
                        .filter(id => newResources.includes(id))

                    setReservation({
                        ...reservation,
                        resourceIds: newResources
                    })
                }}
            />
        )
    }

    return (
        <ReservationDetailsWrapper
            reservation={reservation}
            setReservation={setReservation}
            onSave={onSave}
            onResend={onResend}
            ageCategories={ageCategories}
            resources={resources}
            onClose={onClose}
            onShowFolios={onShowFolios}
            onShowLogs={onShowLogs}
            serviceInfo={serviceInfo}
            onOpenCommunication={onOpenCommunication}
            onOpenFormSelector={onOpenFormSelector}
            originalReservation={originalReservation}
            onSaveAndSendNoShowLink={onSaveAndSendNoShowLink}
            position1Content={(
                <Grid item xs={12} md={12} sx={{alignSelf: 'center'}}>
                    {renderService()}
                </Grid>
            )}
            position2Content={(
                <Grid item xs={12} md={12} sx={{alignSelf: 'center'}}>
                    {renderResources()}
                </Grid>
            )}
            loading={loading}
        >
        </ReservationDetailsWrapper>
    )

}
export default ReservationDetailsAccommodation;
